import { type Dashboard } from '../models/dashboard'
import { DashboardMapper } from '../mappers/dashboard.mappers'
import { UsersTotalsEntity, UsersMetricsEntity } from '../../data/entities/dashboard.entity'
import { type MediaPageEntity, type MediaTotalsEntity } from '../../../engagement/data/entities/media.entity'
import { type EventsTotalsEntity } from '../../../engagement/data/entities/events.entity'
import { type CommunityTotalsEntity } from '../../../engagement/data/entities/community.entity'
import { type ChatTotalsEntity } from '../../../engagement/data/entities/chat.entity'
import { GetInteractor } from '@coris-ts/repository/interactor/get.interactor'
import { GetUsersTotalsQuery, GetUsersMetricsQuery } from '../../data/queries/get-dashboard.query'
import { GetMediaTotalsQuery } from '../../../engagement/data/queries/get-media-totals.query'
import { GetMediaQuery } from '../../../engagement/data/queries/get-media.query'
import { GetEventsTotalsQuery } from '../../../engagement/data/queries/get-events-totals.query'
import { GetCommunityTotalsQuery } from '../../../engagement/data/queries/get-community-totals.query'
import { GetChatTotalsQuery } from '../../../engagement/data/queries/get-chat-totals.query'

export interface GetDashboardParams {
  fromDate: string
  toDate: string
  fromPrevious: string
  toPrevious: string
  organization: string
}

export class GetDashboardInteractor {
  private readonly mapper = new DashboardMapper()

  constructor(
    private readonly getUsersTotals: GetInteractor<UsersTotalsEntity>,
    private readonly getUsersMetrics: GetInteractor<UsersMetricsEntity>,
    private readonly getMediaTotals: GetInteractor<MediaTotalsEntity>,
    private readonly getMediaPage: GetInteractor<MediaPageEntity>,
    private readonly getEventsTotals: GetInteractor<EventsTotalsEntity>,
    private readonly getCommunityTotals: GetInteractor<CommunityTotalsEntity>,
    private readonly getChatTotals: GetInteractor<ChatTotalsEntity>
  ) {}

  public async execute(params: GetDashboardParams): Promise<Dashboard> {
    const [
      usersTotals,
      usersMetrics,
      mediaSongsTotals,
      mediaVideosTotals,
      mostPlayedSongs,
      mostViewedVideos,
      eventsTotals,
      communityTotals,
      chatTotals
    ] = await Promise.all([
      this.getUsersTotals.execute(new GetUsersTotalsQuery(params.organization)),
      this.getUsersMetrics.execute(
        new GetUsersMetricsQuery(
          params.fromDate,
          params.toDate,
          params.fromPrevious,
          params.toPrevious,
          params.organization
        )
      ),
      this.getMediaTotals.execute(
        new GetMediaTotalsQuery(
          params.fromDate,
          params.toDate,
          params.fromPrevious,
          params.toPrevious,
          'song',
          params.organization
        )
      ),
      this.getMediaTotals.execute(
        new GetMediaTotalsQuery(
          params.fromDate,
          params.toDate,
          params.fromPrevious,
          params.toPrevious,
          'video',
          params.organization
        )
      ),
      this.getMediaPage.execute(
        new GetMediaQuery(
          params.fromDate,
          params.toDate,
          params.fromPrevious,
          params.toPrevious,
          'times_played',
          'desc',
          0,
          3,
          'song',
          params.organization
        )
      ),
      this.getMediaPage.execute(
        new GetMediaQuery(
          params.fromDate,
          params.toDate,
          params.fromPrevious,
          params.toPrevious,
          'times_played',
          'desc',
          0,
          3,
          'video',
          params.organization
        )
      ),
      this.getEventsTotals.execute(
        new GetEventsTotalsQuery(
          params.fromDate,
          params.toDate,
          params.fromPrevious,
          params.toPrevious,
          params.organization
        )
      ),
      this.getCommunityTotals.execute(
        new GetCommunityTotalsQuery(
          params.fromDate,
          params.toDate,
          params.fromPrevious,
          params.toPrevious,
          params.organization
        )
      ),
      this.getChatTotals.execute(
        new GetChatTotalsQuery(
          params.fromDate,
          params.toDate,
          params.fromPrevious,
          params.toPrevious,
          params.organization
        )
      )
    ])

    const totals = usersTotals || {
      paid_subscribers: 0,
      free_users: 0,
      free_subscribers: 0,
      incomplete_signups: 0,
      churned: 0,
      apple_subscribers: 0,
      stripe_subscribers: 0,
      total: 0,
      paid_plans: []
    }

    return this.mapper.map({
      overview: {
        total_users: totals.total,
        paid_users: totals.paid_subscribers,
        free_users: totals.free_users,
        free_subscribers: totals.free_subscribers,
        churned_users: totals.churned,
        incomplete_signups: totals.incomplete_signups,
        total_members: totals.total_members,
        active_members: totals.active_members,
        inactive_members: totals.inactive_members,
        paid_plans: totals.paid_plans
      },
      user_metrics: usersMetrics,
      media_totals: {
        songs_played: mediaSongsTotals.times_played,
        videos_viewed: mediaVideosTotals.times_played,
        most_played_songs: mostPlayedSongs.items.map((item) => ({
          id: item.title,
          name: item.title,
          value: item.times_played.current,
          diff: item.times_played.diff,
          image_url: item.icon_url || ''
        })),
        most_viewed_videos: mostViewedVideos.items.map((item) => ({
          id: item.title,
          name: item.title,
          value: item.times_played.current,
          diff: item.times_played.diff,
          image_url: item.icon_url || ''
        }))
      },
      events: {
        buy_now_clicks: eventsTotals.buy_now_clicks,
        presale_codes_redeemed: eventsTotals.presale_codes,
        meet_and_greet_redeemed: eventsTotals.meet_and_greet,
        rsvp_redeemed: eventsTotals.rsvp
      },
      community: {
        viewed_posts: communityTotals.views,
        post_created: communityTotals.posts,
        comments: communityTotals.comments,
        upvotes: communityTotals.upvotes
      },
      chat: {
        active_users: chatTotals.active_users,
        messages_sent: chatTotals.messages,
        unique_messages: chatTotals.unique_chatters
      }
    })
  }
}
